import React from 'react';

/*
 * Progress meter and the small trend chart.
 *
 * Filled marks are ink on the quiet surface, values in mono. A trend is not a
 * status, so it never borrows a semantic colour.
 */

export const ProgressMeter: React.FC<{
  value: number;
  total: number;
  label?: React.ReactNode;
  className?: string;
}> = ({ value, total, label, className = '' }) => {
  const safeTotal = Math.max(total, 1);
  const pct = Math.round((Math.min(Math.max(value, 0), safeTotal) / safeTotal) * 100);
  return (
    <div className={className}>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-[13px] font-semibold text-ink">{label || `${value} of ${total} complete`}</span>
        <span className="font-sans text-[12.5px] tabular-nums text-ink-muted">{pct}%</span>
      </div>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        className="h-2 w-full overflow-hidden rounded-full bg-canvas-inset"
      >
        <div className="h-2 rounded-full bg-ink transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export interface TrendPoint {
  label: string;
  value: number;
}

export const MiniBarChart: React.FC<{
  data: TrendPoint[];
  caption: string;
  format?: (value: number) => string;
  className?: string;
}> = ({ data, caption, format, className = '' }) => {
  if (data.length === 0) return null;
  const peak = Math.max(...data.map((d) => d.value), 1);
  const readValue = format || ((v: number) => String(v));
  const summary = data.map((d) => `${d.label}: ${readValue(d.value)}`).join(', ');
  return (
    <div className={className}>
      <div role="img" aria-label={`${caption}. ${summary}`} className="flex h-[200px] items-end gap-3">
        {data.map((d) => {
          const pct = Math.max(Math.round((d.value / peak) * 100), 2);
          return (
            <div key={d.label} className="flex h-full min-w-0 flex-1 flex-col justify-end">
              <div className="relative h-full w-full overflow-hidden rounded-lg bg-canvas-inset">
                <div
                  className="absolute inset-x-0 bottom-0 rounded-lg bg-ink transition-[height] duration-500"
                  style={{ height: `${pct}%` }}
                  title={`${d.label}: ${readValue(d.value)}`}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2.5 flex gap-3 border-t border-line pt-2.5">
        {data.map((d) => (
          <div key={d.label} className="min-w-0 flex-1 text-center text-[12px] text-ink-dim">{d.label}</div>
        ))}
      </div>
    </div>
  );
};
